"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { ArrowRight } from "lucide-react";

type Destination = { code: string; name: string; price?: string };

/**
 * Re-digital — "Where are you going?" destination CHIP row per
 * generated_homepage.png. Pill chips with flags (not a big grid — the dark
 * brand de-emphasizes long grids per the brief). Client; data passed in.
 */
export default function DestinationChips({ destinations }: { destinations: Destination[] }) {
  const { t } = useTranslations();

  return (
    <section className="container mx-auto px-4 py-8">
      <Reveal as="div" className="dp-card p-6 sm:p-8 grid lg:grid-cols-[1fr_2fr] gap-6 items-center" y={20}>
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-bold text-primary uppercase tracking-wide mb-2">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" /> {t("dp.chips.eyebrow", "Limitless journeys start here")}
          </div>
          <h2 className="text-2xl sm:text-3xl text-foreground">
            {t("dp.chips.title1", "Stay")} <span className="dp-neon">{t("dp.chips.title2", "connected")}</span> {t("dp.chips.title3", "without limits.")}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-sm">{t("dp.chips.sub", "Whether you're exploring cities, working remotely, or chasing sunsets — we keep you connected wherever life takes you.")}</p>
        </div>
        <Stagger className="flex flex-wrap gap-2 items-center" staggerChildren={0.04} amount={0.2}>
          {destinations.slice(0, 8).map((d) => {
            const code = d.code.toLowerCase();
            return (
              <StaggerItem as="div" key={d.code} y={8}>
                <Link href={`/destinations/${code}/`} className="dp-chip inline-flex items-center gap-2 pl-1.5 pr-4 h-10 text-sm font-semibold text-foreground hover:border-primary/50 transition-all hover:-translate-y-0.5">
                  <img src={`https://flagcdn.com/${code}.svg`} alt="" role="presentation" width={24} height={16} className="h-5 w-7 rounded-full object-cover ring-1 ring-border" loading="lazy" referrerPolicy="no-referrer" />
                  {d.name}
                </Link>
              </StaggerItem>
            );
          })}
          <StaggerItem as="div" y={8}>
            <Link href="/destinations/" className="dp-pill inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-bold transition-all active:scale-[0.98]">
              {t("dp.chips.viewAll", "View all")} <ArrowRight className="w-4 h-4" />
            </Link>
          </StaggerItem>
        </Stagger>
      </Reveal>
    </section>
  );
}
